import {Injectable} from '@angular/core';
import {IKioskHardwareEpsonLocalPrinter} from '@core/models/kiosk.model';
import {of, Subject} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError, concatMap} from "rxjs/operators";

@Injectable({
  providedIn: 'root',
})
export class EpsonLocalPrinterService {
  printerIpAddress = '';
  printerDeviceId = '';
  lineWidth = 48;

  constructor(private readonly http: HttpClient) {
  }

  setPrinterConfiguration(printerConfiguration: IKioskHardwareEpsonLocalPrinter): void {
    this.printerIpAddress = printerConfiguration.printer_ip_address;
    this.printerDeviceId = printerConfiguration.printer_device_id;
    this.lineWidth = printerConfiguration.line_width;
  }

  subscribeToPrintLines(printLines$: Subject<string[]>): void {
    printLines$
      .pipe(
        concatMap(lines =>
          this.print(lines).pipe(
            catchError(error => {
              console.error('[EpsonLocalPrinter]: print failed', error);
              return of(null); // continue malgré l’erreur
            })
          )
        )
      )
      .subscribe(response => {
        if (response) {
          console.log('[EpsonLocalPrinter]: success', response);
        }
      });
  }

  print(lines: string[]) {
    const xml = this.convertLinesToXML(lines);
    const url = `http://${this.printerIpAddress}/cgi-bin/epos/service.cgi?devid=${this.printerDeviceId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/xml',
    });

    console.log('[EpsonLocalPrinter] Print', {lines}, xml);
    return this.http.post(url, xml, {headers, responseType: 'text'});
  }

  private convertLinesToXML(lines: string[]) {
    // XML de départ
    let xml = `<?xml version="1.0" encoding="UTF-8"?>
  <s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
    <s:Body>
      <epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
  `;

    lines.forEach(line => {
      const escapedLine = this.escapeXml(line);
      xml += `<text x="0" font="font_a" em="false" reverse="false" dh="false" dw="false" align="left">${escapedLine}</text>
  <feed line="1"/>`;
    });

    xml += `
        <cut type="feed"/>
      </epos-print>
    </s:Body>
  </s:Envelope>`;
    return xml;
  }

  // Normalize special characters for printer compatibility
  private normalizeSpecialCharacters(text: string): string {
    const charMap: { [key: string]: string } = {
      'à': 'a', 'â': 'a', 'ä': 'a', 'á': 'a', 'ã': 'a', 'å': 'a',
      'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
      'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
      'ò': 'o', 'ó': 'o', 'ô': 'o', 'ö': 'o', 'õ': 'o', 'ø': 'o',
      'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
      'ý': 'y', 'ÿ': 'y',
      'ñ': 'n',
      'ç': 'c',
      'À': 'A', 'Â': 'A', 'Ä': 'A', 'Á': 'A', 'Ã': 'A', 'Å': 'A',
      'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
      'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
      'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Ö': 'O', 'Õ': 'O', 'Ø': 'O',
      'Ù': 'U', 'Ú': 'U', 'Û': 'U', 'Ü': 'U',
      'Ý': 'Y',
      'Ñ': 'N',
      'Ç': 'C'
    };

    return text.replace(/[àâäáãåèéêëìíîïòóôöõøùúûüýÿñçÀÂÄÁÃÅÈÉÊËÌÍÎÏÒÓÔÖÕØÙÚÛÜÝÑÇ]/g, (char) => {
      return charMap[char] || char;
    });
  }

  // Fonction pour échapper les caractères spéciaux dans le XML
  private escapeXml(unsafe: string): string {
    // First normalize special characters for printer compatibility
    const normalizedText = this.normalizeSpecialCharacters(unsafe);

    return normalizedText.replace(/[<>&"']/g, function (char) {
      switch (char) {
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '&':
          return '&amp;';
        case '"':
          return '&quot;';
        case "'":
          return '&apos;';
        default:
          return char;
      }
    });
  }
}

